import { Grid2, Stack } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Common, UserAdminControl } from '@components/Admin';
import ProfileCard from '@components/ProfileCard';

import { usePageContext } from '@contexts/page-context';

import { useGetUser } from '@hooks/api/user';

import { formatDate, formatName } from '@utils/format';
import { mapProfile } from '@utils/profiles';

const messages = defineMessages({
  name: {
    id: 'admin.users.name',
    defaultMessage: 'Name',
  },
  email: {
    id: 'admin.users.email',
    defaultMessage: 'Email',
  },
  lastLogin: {
    id: 'admin.users.lastLogin',
    defaultMessage: 'Last Login',
  },
  createdAt: {
    id: 'admin.users.createdAt',
    defaultMessage: 'Created At',
  },
  roles: {
    id: 'admin.users.roles',
    defaultMessage: 'Roles',
  },
});

interface UserDrilldownProps {
  user_id: string;
}

const UserDrilldown = ({
  user_id,
}: React.PropsWithChildren<UserDrilldownProps>) => {
  const { formatMessage } = useIntl();
  const { data: userData, loading } = useGetUser({ id: user_id });
  const user = mapProfile(userData?.getUserRecord);
  const { setTitle } = usePageContext();

  React.useEffect(() => {
    if (user?.user_id) {
      const { first_name, last_name } = user;
      const newTitle = formatName(first_name, last_name);
      if (newTitle) {
        setTitle(newTitle);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.first_name, user?.last_name]);

  const data: Common.Card.IMetadata.Metadata[] = [
    {
      title: formatMessage(messages.email),
      value: user?.email,
    },
    {
      title: formatMessage(messages.lastLogin),
      value: formatDate({
        date: user?.last_login,
        formatter: 'datetime',
        fallback: '-',
      }),
    },
  ];

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 12, sm: 6 }}>
        <Stack spacing={1}>
          <ProfileCard
            name={formatName(user?.title, user?.first_name, user?.last_name)}
            profile_picture={user?.profile_picture}
          />
          <Common.Card.Metadata data={data} loading={loading} />
        </Stack>
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6 }}>
        <UserAdminControl.RoleEditor user={user} loading={loading} />
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <UserAdminControl.Settings user_id={user?.user_id} />
      </Grid2>
      {/* TODO Delete user once internal roles are all under one account */}
      {/* <UserAdminControl.DangerZone user={user} /> */}
    </Grid2>
  );
};

export default UserDrilldown;
